'use client';

import classNames from 'classnames';
import { ReactNode, useMemo } from 'react';

import { PropsWithClassname } from '@/app/types';

import Sticky from 'react-sticky-el';
import { useTranslation } from 'react-i18next';

export interface Tab<TabIdType> {
  id: TabIdType;
  label: string;
}

interface TabsProps<TabIdType> extends PropsWithClassname {
  tabs: Tab<TabIdType>[];
  activeTabId: TabIdType;
  additionalContent?: ReactNode;
  isStickyOnDesktop?: boolean;
  onSelectTab: (tabId: TabIdType) => void;
  onToggleElementFixed?: (isFixed: boolean) => void;
}

function Tabs<TabIdType = string | number>({
  tabs,
  activeTabId,
  additionalContent,
  isStickyOnDesktop,
  onSelectTab,
  onToggleElementFixed,
  className,
}: TabsProps<TabIdType>) {
  const {
    i18n: { language },
  } = useTranslation();

  const children = useMemo(
    () => (
      <div className="flex mx-[auto] w-full default-page-max-width px-[24px] justify-between items-center">
        <div className="flex">
          {tabs.map((tab, index) => (
            <button
              data-testid={`tab-${String(tab.id)}`}
              className={classNames(
                'text-[14px] lg:text-[18px] whitespace-nowrap pt-[24px] pb-[20px] border-b-[2px] transition-all hover:text-primary-mid',
                {
                  'border-primary-mid': tab.id === activeTabId,
                  'border-white': tab.id !== activeTabId,
                  'mr-[16px] lg:mr-[32px]': index < tabs.length,
                },
              )}
              key={String(tab.id)}
              onClick={() => onSelectTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {additionalContent}
      </div>
    ),
    [additionalContent, onSelectTab, activeTabId, tabs, language],
  );

  return (
    <>
      {isStickyOnDesktop && (
        <div className={classNames(className, 'hidden lg:block w-full')}>
          <Sticky
            stickyClassName="bg-white overflow-y-auto border-b border-neutral-light z-10"
            onFixedToggle={onToggleElementFixed}
          >
            {children}
          </Sticky>
        </div>
      )}
      <div
        className={classNames(className, 'block w-full bg-white overflow-y-auto border-b border-neutral-light z-10', {
          'lg:hidden': isStickyOnDesktop,
        })}
      >
        {children}
      </div>
    </>
  );
}

export default Tabs;
